import React, { useState } from 'react';
import { Row, Col, Card, Button, Alert, Form } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const ACCOUNT_LEVELS = [
  { value: 'testing', label: 'Testing', tokens: '1,000' },
  { value: 'standard', label: 'Standard', tokens: '50,000' },
  { value: 'professional', label: 'Professional', tokens: '125,000' },
  { value: 'enterprise', label: 'Enterprise', tokens: '300,000' }
];

const PRODUCTS = [
  { id: 'article_to_media', name: 'Article to Media', available: true },
  { id: 'audio_to_media', name: 'Audio to Media', available: false },
  { id: 'live_audio_to_media', name: 'Live Audio to Media', available: false }
];

const TenantCreate = ({ auth }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    account_level: 'testing',
    products_enabled: ['article_to_media'],
    initial_admin_email: '',
    initial_admin_display_name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  });

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: generateSlug(name)
    });
  };

  const handleProductToggle = (productId) => {
    const current = formData.products_enabled;
    if (current.includes(productId)) {
      setFormData({
        ...formData,
        products_enabled: current.filter(p => p !== productId)
      });
    } else {
      setFormData({
        ...formData,
        products_enabled: [...current, productId]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError('Company name is required');
      return;
    }

    if (!formData.slug.trim()) {
      setError('Slug is required');
      return;
    }

    if (formData.products_enabled.length === 0) {
      setError('At least one product must be enabled');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch('/api/admin/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Tenant "${formData.name}" created successfully!`);
        setTimeout(() => {
          history.push(`/admin/tenants/${data.tenant.id}`);
        }, 1500);
      } else {
        setError(data.error || 'Failed to create tenant');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Common timezones for dropdown
  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Australia/Sydney',
    'UTC'
  ];

  return (
    <TenantManagementLayout title="Create Tenant" subtitle="Set up a new organization account">
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Organization Details</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="Acme Corporation"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Slug *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="acme-corporation"
                    required
                  />
                  <Form.Text className="text-muted">
                    Used in URLs and for identification. Auto-generated from name.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Level *</Form.Label>
                  <Form.Select
                    value={formData.account_level}
                    onChange={(e) => setFormData({ ...formData, account_level: e.target.value })}
                  >
                    {ACCOUNT_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label} ({level.tokens} tokens/month)
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Timezone</Form.Label>
                  <Form.Select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  >
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    All dates/times will be displayed in this timezone.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Products Enabled</h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Select which products this tenant can access.
            </p>
            {PRODUCTS.map(product => (
              <Form.Check
                key={product.id}
                type="checkbox"
                id={`product-${product.id}`}
                className="mb-2"
                disabled={!product.available}
                label={
                  <span style={{ opacity: product.available ? 1 : 0.6 }}>
                    {product.name}
                    {!product.available && (
                      <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>
                        (Coming Soon)
                      </span>
                    )}
                  </span>
                }
                checked={formData.products_enabled.includes(product.id)}
                onChange={() => product.available && handleProductToggle(product.id)}
              />
            ))}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Initial Admin (Optional)</h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Optionally invite the first admin user for this tenant. They will receive an invitation email.
            </p>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Admin Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.initial_admin_email}
                    onChange={(e) => setFormData({ ...formData, initial_admin_email: e.target.value })}
                    placeholder="admin@acme.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.initial_admin_display_name}
                    onChange={(e) => setFormData({ ...formData, initial_admin_display_name: e.target.value })}
                    placeholder="John Smith"
                  />
                  <Form.Text className="text-muted">
                    How they will appear in the tenant. Defaults to email username.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => history.push('/admin/tenants')}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </Form>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantCreate);
